import React from 'react';
import { TopCategoryProductsCarouselContainer, StyledContainer, StyledContents, StyledTitle, styles } from './SubcategorySpotlight.styles';
import { TopCategoryProductsCarousel } from '../TopCategoryProductsCarousel/TopCategoryProductsCarousel';
import type { ExplodedCategoryProps } from '../../ExplodedCategory';
import { Header } from '../../../RecommendationsCarousel';
import { SkeletonText } from '@shared/components/Skeleton';
import { CategoryRecommendationsCarousel } from '../../../CategoryRecommendationsCarousel/CategoryRecommendationsCarousel';
import { Box } from '@withjoy/joykit';
import { VimeoVideo, useOptimizedVimeoReference } from '@shared/components/VimeoVideo';
interface SubcategorySpotlightProps {
  title: string;
  subcategory: ExplodedCategoryProps['subcategories'][number];
  onBrowseCategoryClick: ExplodedCategoryProps['onBrowseCategoryClick'];
  zIndex?: number;
}

const SubcategorySpotlight = ({ title, subcategory, onBrowseCategoryClick, zIndex }: SubcategorySpotlightProps) => {
  const backgroundImageUrl = subcategory.spotlight?.image?.url;
  const backgroundVideoUrl = subcategory.spotlight?.videoUrl || undefined;

  return (
    <StyledContainer
      __css={styles.container}
      zIndex={zIndex}
    >
      <StyledContents __css={styles.contents}>
        <Header enablePeek={true}>
          <StyledTitle
            __css={styles.title}
            typographyVariant={['Hed40', null, 'Hed56']}
            fontWeight={'light'}
          >
            {title}
          </StyledTitle>
        </Header>
        <TopCategoryProductsCarouselContainer __css={styles.topCategoryProductsCarousel}>
          <TopCategoryProductsCarousel
            category={subcategory}
            onBrowseCategoryClick={onBrowseCategoryClick}
            variant="spotlight"
          />
        </TopCategoryProductsCarouselContainer>
      </StyledContents>
      <SubcategorySpotlightBackground
        title={title}
        backgroundImageUrl={backgroundImageUrl}
        backgroundVideoUrl={backgroundVideoUrl}
      />
    </StyledContainer>
  );
};

interface SubcategorySpotlightBackgroundProps {
  title: string;
  backgroundImageUrl?: string;
  backgroundVideoUrl?: string;
}

const SubcategorySpotlightBackground = ({ title, backgroundImageUrl, backgroundVideoUrl }: SubcategorySpotlightBackgroundProps) => {
  const { video: optimizedVideo, isFlagLoading: isVideoFlagLoading } = useOptimizedVimeoReference(backgroundVideoUrl);

  return (
    <Box __css={styles.mediaContainer}>
      <Box __css={styles.gradient} />
      {backgroundVideoUrl && (
        <Box __css={styles.backgroundVideo}>
          <Box __css={styles.backgroundVideoContainer}>
            {isVideoFlagLoading ? null : optimizedVideo ? (
              <Box __css={styles.videoCover}>
                <VimeoVideo
                  videoId={optimizedVideo.videoId}
                  privacyHash={optimizedVideo.privacyHash}
                  title={title}
                />
              </Box>
            ) : (
              <iframe
                src={`${backgroundVideoUrl}&background=1`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                loading="lazy"
                allowFullScreen
                title={`${title}`}
              />
            )}
          </Box>
        </Box>
      )}
      {!backgroundVideoUrl && backgroundImageUrl && <Box __css={styles.backgroundImage(backgroundImageUrl)} />}
    </Box>
  );
};

SubcategorySpotlight.Skeleton = () => {
  return (
    <StyledContainer>
      <StyledContents __css={styles.skeletonContents}>
        <Header enablePeek={true}>
          <SkeletonText
            rows={2}
            skeletonHeight={56}
            width={[200, 500]}
            marginBottom={10}
            marginTop={10}
          />
        </Header>
        <TopCategoryProductsCarouselContainer __css={styles.topCategoryProductsCarousel}>
          <CategoryRecommendationsCarousel.Skeleton />
        </TopCategoryProductsCarouselContainer>
      </StyledContents>
    </StyledContainer>
  );
};

export { SubcategorySpotlight };
